import { Component, TemplateRef, ViewChild, computed, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Role, UserAdmin, UserSearchEvent } from '../../shared/models/user.model';
import { BatchTasks } from '../../shared/services/batch-tasks';
import { ModalService } from '../../shared/services/modal.service';
import { NotificationService } from '../../shared/services/notification.service';
import { UserActionData, UsersService } from '../../shared/services/users.service';
import { UsersSearchComponent } from '../users-search/users-search.component';
import { UsersTableComponent } from '../users-table/users-table.component';

@Component({
  standalone: false,
  selector: 'app-users-index',
  templateUrl: 'users-index.component.html',
  styleUrls: ['users-index.component.scss'],
})
export class UsersIndexComponent {
  @ViewChild(UsersSearchComponent, { static: false }) userSearchComponent: UsersSearchComponent;
  @ViewChild(UsersTableComponent, { static: false }) usersTableComponent: UsersTableComponent;
  @ViewChild('batchAgreementModalTpl', { read: TemplateRef, static: false }) batchAgreementModalTpl: TemplateRef<HTMLTemplateElement>;

  spinner = false;

  readonly selectedRows = signal<UserAdmin[]>([]);
  batchCompleted = 0;
  batchTaskTotal = 0;
  progressShow = false;
  readonly batchAgreementAction = signal<'customAgreement' | 'clicktermAgreement' | null>(null);
  readonly batchAgreementEligibleUsers = computed(() =>
    this.selectedRows().filter(row => row.role === Role.MAIN && !row.clicktermAgreementFlowEnabled && !row.customAgreement)
  );
  readonly batchAgreementCount = computed(() => this.batchAgreementEligibleUsers().length);
  readonly batchAgreementModalTitle = computed(() =>
    this.batchAgreementAction() === 'clicktermAgreement' ? 'Enable ClickTerm agreement' : 'Set custom agreement'
  );
  readonly batchAgreementModalText = computed(() =>
    this.batchAgreementAction() === 'clicktermAgreement'
      ? 'Clickterm flow will be enabled for these users:'
      : 'Custom accepted agreement will be set for these users:'
  );
  readonly batchAgreementUserEmails = computed(() => this.batchAgreementEligibleUsers().map(user => user.email));

  constructor(
    public userService: UsersService,
    public router: Router,
    public notificationService: NotificationService,
    titleService: Title,
    public modal: ModalService
  ) {
    titleService.setTitle('Users');
  }

  userAction(event: UserActionData) {
    if (event.name === 'edit') {
      this.router.navigate(['users', event.row.id]).then();
    }
    if (event.name === 'accountClick') {
      const phrase = event.row.parentEmail ? event.row.parentEmail : 'parent:' + event.row.email;
      this.userSearchComponent.setSearchFormControlValue(phrase);
    }
  }

  userCreate() {
    this.router.navigate(['users/create']).then();
  }

  batchClear(): void {
    this.selectedRows.set([]);
    this.usersTableComponent.resetBatch();
  }

  onBatchDelete() {
    this.modal
      .confirm()
      .open()
      .result.then(res => {
        if (!res) {
          return;
        }
        this.batchDelete();
      });
  }

  private batchDelete() {
    const tasks = this.selectedRows().map(row => {
      return this.userService.delete(row.id).pipe(
        map(() => {
          this.notificationService.success(`Deleted user ${row.email}`, 'Users');
          this.completeTask();
        }),
        catchError(() => {
          this.notificationService.error(`Delete user ${row.email} an error`, 'Users');
          this.completeTask();
          return EMPTY;
        })
      );
    });
    this.startTasks(tasks);
  }

  onBatchEnabled(enabled: boolean) {
    const text = enabled
      ? 'Once enabled, the account will be able to view or issue tests.'
      : 'Once disabled, the account will not be able to view or issue tests.';
    this.modal
      .confirm('Are you sure?', text)
      .open()
      .result.then(res => {
        if (!res) {
          return;
        }
        this.batchEnabled(enabled);
      });
  }

  onBatchEnableClicktermAgreement() {
    this.openBatchAgreementModal('clicktermAgreement');
  }

  onBatchEnableCustomAgreement() {
    this.openBatchAgreementModal('customAgreement');
  }

  private batchEnabled(enabled: boolean) {
    const tasks = this.selectedRows().map(row => {
      return this.userService.updateEnable(row.id, enabled).pipe(
        map(() => {
          const text = (enabled ? 'Enabled user ' : 'Disabled user ') + row.email;
          this.notificationService.success(text, 'Users');
          this.completeTask();
        }),
        catchError(() => {
          const text = enabled ? `Enabled user ${row.email} an error` : `Disabled user ${row.email} an error`;
          this.notificationService.error(text, 'Users');
          this.completeTask();
          return EMPTY;
        })
      );
    });
    this.startTasks(tasks);
  }

  private openBatchAgreementModal(action: 'customAgreement' | 'clicktermAgreement') {
    this.batchAgreementAction.set(action);

    if (!this.batchAgreementEligibleUsers().length) {
      this.resetBatchAgreementModalState();
      return;
    }

    const dialogRef = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.batchAgreementModalTpl).open();
    dialogRef.result.then(result => {
      if (!result || !this.batchAgreementEligibleUsers().length || !this.batchAgreementAction()) {
        this.resetBatchAgreementModalState();
        return;
      }

      this.batchUpdateAgreementSettings(this.batchAgreementAction()!, this.batchAgreementEligibleUsers());
      this.resetBatchAgreementModalState();
    });
  }

  private batchUpdateAgreementSettings(action: 'customAgreement' | 'clicktermAgreement', users: UserAdmin[]) {
    const tasks = users.map(row => {
      const settings =
        action === 'clicktermAgreement'
          ? { clicktermAgreementFlowEnabled: true }
          : { clicktermAgreementFlowEnabled: row.clicktermAgreementFlowEnabled, customAgreement: true };

      return this.userService.updateClicktermAgreementSettings(row.id, settings).pipe(
        map(() => {
          if (action === 'clicktermAgreement') {
            this.notificationService.success(`Clickterm agreement flow enabled for user ${row.email}`, 'Users');
          } else {
            this.notificationService.success(`Custom agreement accepted set for user ${row.email}`, 'Users');
          }
          this.completeTask();
        }),
        catchError(() => {
          if (action === 'clicktermAgreement') {
            this.notificationService.error(`Enable Clickterm agreement flow for user ${row.email} failed`, 'Users');
          } else {
            this.notificationService.error(`Set custom agreement accepted for user ${row.email} failed`, 'Users');
          }
          this.completeTask();
          return EMPTY;
        })
      );
    });

    this.selectedRows.set(users);
    this.startTasks(tasks);
  }

  private resetBatchAgreementModalState() {
    this.batchAgreementAction.set(null);
  }

  startTasks(tasks: Observable<unknown>[]) {
    this.batchCompleted = 0;
    this.batchTaskTotal = tasks.length;
    this.usersTableComponent.loading = true;
    new BatchTasks(tasks).run(4);
  }

  completeTask() {
    this.batchCompleted++;
    if (this.batchTaskTotal === this.batchCompleted) {
      this.usersTableComponent.loading = false;
      this.batchTaskTotal = 0;
      this.selectedRows.set([]);
      this.usersTableComponent.resetBatch();
      this.usersTableComponent.update();
    }
  }

  onSubmitSearchForm(event: UserSearchEvent) {
    if (this.usersTableComponent.loading) {
      return;
    }
    this.usersTableComponent.requestParams.search = event.searchString || '';
    delete event.searchString;

    const isEmpty = Object.values(event).every(x => x === null || x === '' || x?.length === 0);
    this.usersTableComponent.userSearchObject = isEmpty ? null : event;
    this.usersTableComponent.currentPageNumber = 1;
    this.usersTableComponent.update();
  }
}
